import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React, { ReactNode } from "react";

import { BlogCtaButton } from "./BlogCtaButton/BlogCtaButton";
import * as styles from "./styles.css";
import { ITestimonial } from "./TestimonialContent";
import { TestimonialTilesCarousel } from "./TestimonialTilesCarousel/TestimonialTilesCarousel";

interface ITestimonialProps {
  itemsMobile: ITestimonial[];
  itemsDesktop: ITestimonial[];
  brandCountHeader: ReactNode | undefined;
}

export const Testimonials = React.forwardRef<HTMLDivElement, ITestimonialProps>(
  ({ itemsMobile, itemsDesktop, brandCountHeader }, ref) => {
    return (
      <Column
        align="center"
        justify="center"
        className={styles.testimonialsWrapper}
        style={{ backgroundColor: Decorative.tint.neutral }}
        ref={ref}
      >
        <Column
          align="center"
          justify="space-evenly"
          className={styles.carouselWrapper}
        >
          {brandCountHeader ? (
            <>
              <Typography
                color={Core.text.subdued}
                align="center"
                style={{ letterSpacing: "1.25px" }}
              >
                {brandCountHeader}
              </Typography>
              <Spacer height="3x" />
            </>
          ) : null}
          <ResponsiveContentV2
            mobile={
              <TestimonialTilesCarousel
                name="logged-out-brand-page-testimonials-mobile"
                items={itemsMobile}
                testimonialTiles={testimonialTiles(itemsMobile)}
              />
            }
            tabletAndAbove={
              <TestimonialTilesCarousel
                name="logged-out-brand-page-testimonials-desktop"
                items={itemsDesktop}
                testimonialTiles={testimonialTiles(itemsDesktop)}
              />
            }
          />
        </Column>
      </Column>
    );
  }
);

const testimonialTiles = (items: ITestimonial[]): React.JSX.Element[] => {
  const testimonials = items;

  return testimonials.map((testimonial, index) => {
    return <TestimonialTile key={index} testimonial={testimonial} />;
  });
};

interface ITestimonialTileProps {
  testimonial: ITestimonial;
}

const TestimonialTile: React.FC<ITestimonialTileProps> = ({ testimonial }) => {
  return (
    <Column align="center" justify="center">
      <img
        className={styles.avatar}
        src={optimizeImageUrlFillSquare(
          { url: testimonial.avatarImageUrl },
          true,
          72,
          { serverOnly: true }
        )}
      />
      <Spacer height="2x" />
      <Typography align="center" variant="pageHeaderSerifRegular" as="h3">
        {testimonial.content}
      </Typography>
      <Spacer height="3x" />
      <Typography align="center">{testimonial.reviewerInfo}</Typography>
      {testimonial.blogCta ? (
        <>
          <Spacer height="2x" />
          <BlogCtaButton
            blogCta={testimonial.blogCta}
            reviewerInfo={testimonial.reviewerInfo}
          />
        </>
      ) : null}
    </Column>
  );
};
